import React from 'react';
import { COLORS } from '../../../../utils/constants';
import { X } from 'lucide-react';

// StatCard Component
export const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
  <div style={{ backgroundColor: COLORS.card, borderRadius: '12px', padding: '16px', border: `1px solid ${COLORS.border}` }}>
    {Icon && (
      <div style={{ 
        width: '40px', 
        height: '40px', 
        borderRadius: '8px', 
        backgroundColor: `${color}15`, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        marginBottom: '8px'
      }}>
        <Icon size={20} color={color} />
      </div>
    )}
    <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '4px' }}>{label}</div>
    <div style={{ color, fontSize: '22px', fontWeight: '700' }}>{value}</div>
    {subtext && <div style={{ color: COLORS.success, fontSize: '11px' }}>{subtext}</div>}
  </div>
);

// Modal Wrapper
export const ModalWrapper = ({ title, children, onClose, wide = false }) => (
  <div 
    style={{ 
      position: 'fixed', 
      inset: 0, 
      backgroundColor: 'rgba(0,0,0,0.7)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000 
    }} 
    onClick={onClose}
  >
    <div 
      style={{ 
        backgroundColor: COLORS.card, 
        borderRadius: '16px', 
        padding: '24px', 
        width: '100%', 
        maxWidth: wide ? '600px' : '450px', 
        maxHeight: '90vh', 
        overflowY: 'auto', 
        margin: '20px' 
      }} 
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ color: COLORS.text, fontSize: '18px', fontWeight: '700', margin: 0 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: COLORS.textSecondary, cursor: 'pointer' }}>
          <X size={20} />
        </button>
      </div>
      {children}
    </div>
  </div>
);

// Form Input
export const FormInput = ({ label, value, onChange, type = 'text', placeholder = '' }) => (
  <div>
    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>{label}</label>
    <input 
      type={type} 
      value={value} 
      onChange={(e) => onChange(e.target.value)} 
      placeholder={placeholder} 
      style={{ 
        width: '100%', 
        padding: '12px', 
        backgroundColor: COLORS.background, 
        border: `1px solid ${COLORS.border}`, 
        borderRadius: '8px', 
        color: COLORS.text, 
        fontSize: '14px', 
        boxSizing: 'border-box' 
      }} 
    />
  </div>
);

// Form Select
export const FormSelect = ({ label, value, onChange, options }) => (
  <div>
    <label style={{ display: 'block', color: COLORS.textSecondary, fontSize: '13px', marginBottom: '6px' }}>{label}</label>
    <select 
      value={value} 
      onChange={(e) => onChange(e.target.value)} 
      style={{ 
        width: '100%', 
        padding: '12px', 
        backgroundColor: COLORS.background, 
        border: `1px solid ${COLORS.border}`, 
        borderRadius: '8px', 
        color: COLORS.text, 
        fontSize: '14px' 
      }}
    >
      {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  </div>
);

// Tab Button
export const TabButton = ({ active, onClick, label }) => (
  <button 
    onClick={onClick} 
    style={{ 
      padding: '8px 16px', 
      backgroundColor: active ? COLORS.primary : COLORS.background, 
      border: 'none', 
      borderRadius: '8px', 
      color: active ? '#000' : COLORS.textSecondary, 
      fontSize: '13px', 
      fontWeight: '600', 
      cursor: 'pointer' 
    }}
  >
    {label}
  </button>
);

// Error Message
export const ErrorMessage = ({ message }) => (
  <div style={{ 
    padding: '10px', 
    backgroundColor: `${COLORS.danger}20`, 
    borderRadius: '8px', 
    color: COLORS.danger, 
    fontSize: '13px', 
    marginBottom: '16px' 
  }}>
    {message}
  </div>
);

// Submit Button
export const SubmitButton = ({ onClick, loading, label }) => (
  <button 
    onClick={onClick} 
    disabled={loading} 
    style={{ 
      width: '100%', 
      marginTop: '20px', 
      padding: '14px', 
      backgroundColor: loading ? COLORS.border : COLORS.primary, 
      border: 'none', 
      borderRadius: '8px', 
      color: loading ? COLORS.textSecondary : '#000', 
      fontSize: '15px', 
      fontWeight: '600', 
      cursor: loading ? 'not-allowed' : 'pointer' 
    }}
  >
    {loading ? 'Обработка...' : label}
  </button>
);

// Toggle Switch
export const ToggleSwitch = ({ value, onChange }) => (
  <button 
    onClick={onChange} 
    style={{ 
      width: '44px', 
      height: '24px', 
      borderRadius: '12px', 
      backgroundColor: value ? COLORS.success : COLORS.border, 
      border: 'none', 
      cursor: 'pointer', 
      position: 'relative' 
    }}
  >
    <div style={{ 
      width: '20px', 
      height: '20px', 
      borderRadius: '50%', 
      backgroundColor: '#fff', 
      position: 'absolute', 
      top: '2px', 
      left: value ? '22px' : '2px', 
      transition: 'left 0.2s' 
    }} />
  </button>
);

// Role helpers
export const getRoleColor = (role) => {
  const colors = {
    super_admin: '#f43f5e',
    admin: '#8b5cf6',
    manager: '#3b82f6',
    support: '#14b8a6',
  };
  return colors[role] || COLORS.textSecondary;
};

export const getRoleLabel = (role) => {
  const labels = {
    super_admin: 'Супер Админ',
    admin: 'Админ',
    manager: 'Менеджер',
    support: 'Поддержка',
  };
  return labels[role] || role;
};
